import { useEffect, useRef, useState } from 'react';

import { useUrlSearchParams } from 'utils/customHooks/useUrlSearchParams';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { getOpParams } from 'utils/getOpParams';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';

import { MigratedInfoModal } from './MigratedInfoModal';

/**
 * @component
 * @description Wrapper component that manages the visibility logic for MigratedInfoModal.
 * This component must be rendered inside a Router context.
 */
export const MigratedInfoModalWrapper = () => {
  const { service } = useUrlSearchParams();
  const { orgId } = getOpParams();
  const [isMigratedInfoModalOpen, setIsMigratedInfoModalOpen] = useState(false);
  const serviceRef = useRef(service);
  const { identityId, isLoading } = useIdentityAndUser();

  /**
   * As of today there are 733 partner orgs in Access that are not migrated. We built this modal
   * assuming that the partner would have started their login flow from DMP but that is not necessarily
   * the case. This feature flag is used to determine if the modal should be shown for orgs that are migrated
   * but they simply went to the /signin page thus no query param would exist to trigger the modal.
   */
  const { value: orgsToShowModal, isLoading: isLoadingFeatureFlag } =
    useFeatureFlag<number[]>(
      'PARTNER_ORGS_TO_SHOW_MODAL_POST_MIGRATION',
      orgId,
    );

  const isOrgInList = orgsToShowModal?.includes(orgId);

  const { data: orgData, isLoading: isLoadingOrgData } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (data) => data.json.data,
  });

  /**
   * Use this in case FOR WHATEVER REASON the partner was not turned into a video partner.
   * This should likely never be the case as the only partners we didn't migrate to DMP that
   * we had already known about were not migrated because they had customers with the Video
   * app installed and configured. But best to be safe here. It simply drives a text change in the modal.
   */
  const isVideoPartner = Boolean(orgData?.orgAwareConfig?.dmpOrgIdExt);

  const localStorageKey = `hasSeenMigratedInfoModal-${identityId}`;

  if (!serviceRef.current && service) {
    serviceRef.current = service;
  }

  // Check if the migrated info modal should be shown
  useEffect(() => {
    if (isLoading || isLoadingOrgData || isLoadingFeatureFlag) return;

    const hasSeenModal = localStorage.getItem(localStorageKey);
    if (
      (serviceRef.current === 'dmp' || service === 'dmp' || isOrgInList) &&
      hasSeenModal !== 'true'
    ) {
      setIsMigratedInfoModalOpen(true);
    }
  }, [
    service,
    localStorageKey,
    isLoading,
    isLoadingOrgData,
    isLoadingFeatureFlag,
    isOrgInList,
  ]);

  const handleMigratedInfoModalClose = () => {
    localStorage.setItem(localStorageKey, 'true');
    setIsMigratedInfoModalOpen(false);
  };

  return (
    <MigratedInfoModal
      open={isMigratedInfoModalOpen}
      onCancel={handleMigratedInfoModalClose}
      isVideoPartner={isVideoPartner}
    />
  );
};
